import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RECORD_STATUS } from '../../config';
import { Record } from '../../config/Record/RecordEntity';

export const useCounterAppraisalAnalysis = () => {
  const { recordId } = useParams();
  const record = useSelector((state) => state.reduxRecords.data[recordId]);
  const recordEntity = new Record(record);
  const { counter_appraisal_verification: counterAppraisalVerification } = RECORD_STATUS;
  const { expertiseCosts, vehicle, vehicleExpertise, supervisor, counterInspection } =
    useSelector((state) => state.reduxRecords.data[recordId]) || {};
  const countryPosId = useSelector((state) => state.reduxRecords.data[recordId]?.carcheckPointOfSale?.countryId);
  const [tooltipOpenSecond, setTooltipOpenSecond] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);

  const calculateCosts = (costs) => {
    return {
      bodyCosts: parseFloat(costs?.bodyCosts) || 0,
      pneumaticCosts: parseFloat(costs?.pneumaticCosts) || 0,
      servicingCosts: parseFloat(costs?.servicingCosts) || 0,
      roadTestCosts: parseFloat(costs?.roadTestCosts) || 0,
      innerCosts: parseFloat(costs?.innerCosts) || 0,
      mechanicalCosts: parseFloat(costs?.mechanicalCosts) || 0,
    };
  };

  const calculateGap = (inspection, counterInsp, key) => {
    const inspectionValue = parseFloat(inspection?.[key]) || 0;
    const counterInspValue = parseFloat(counterInsp?.[key]) || 0;
    return counterInspValue - inspectionValue;
  };

  const calculateTotalCost = (costs) => {
    return Object.values(costs).reduce((total, cost) => total + cost, 0);
  };
  const reconditionCostInspection = useMemo(
    () => calculateTotalCost(calculateCosts(expertiseCosts)),
    [
      expertiseCosts?.bodyCosts,
      expertiseCosts?.pneumaticCosts,
      expertiseCosts?.servicingCosts,
      expertiseCosts?.roadTestCosts,
      expertiseCosts?.innerCosts,
      expertiseCosts?.mechanicalCosts,
    ],
  );
  const reconditionCostCounterInspection = useMemo(
    () => calculateTotalCost(calculateCosts(counterInspection)),
    [
      counterInspection?.bodyCosts,
      counterInspection?.pneumaticCosts,
      counterInspection?.servicingCosts,
      counterInspection?.roadTestCosts,
      counterInspection?.innerCosts,
      counterInspection?.mechanicalCosts,
    ],
  );

  const MileageGap = useMemo(
    () => calculateGap(vehicleExpertise, counterInspection, 'mileage'),
    [counterInspection?.mileage, vehicleExpertise?.mileage],
  );

  const gapBodywork = useMemo(
    () => calculateGap(expertiseCosts, counterInspection, 'bodyCosts'),
    [counterInspection?.bodyCosts, expertiseCosts?.bodyCosts],
  );

  const gapServicing = useMemo(
    () => calculateGap(expertiseCosts, counterInspection, 'servicingCosts'),
    [counterInspection?.servicingCosts, expertiseCosts?.servicingCosts],
  );

  const gapWheels = useMemo(
    () => calculateGap(expertiseCosts, counterInspection, 'pneumaticCosts'),
    [counterInspection?.pneumaticCosts, expertiseCosts?.pneumaticCosts],
  );

  const gapInterior = useMemo(
    () => calculateGap(expertiseCosts, counterInspection, 'innerCosts'),
    [counterInspection?.innerCosts, expertiseCosts?.innerCosts],
  );
  const gapRoadTest = useMemo(
    () => calculateGap(expertiseCosts, counterInspection, 'roadTestCosts'),
    [counterInspection?.roadTestCosts, expertiseCosts?.roadTestCosts],
  );

  const gapMotor = useMemo(
    () => calculateGap(expertiseCosts, counterInspection, 'mechanicalCosts'),
    [counterInspection?.mechanicalCosts, expertiseCosts?.mechanicalCosts],
  );

  const newSuggestOffer = useMemo(() => {
    const c2bOffer = parseFloat(supervisor?.c2bOffer) || 0;
    const gapReconditionCost = reconditionCostCounterInspection - reconditionCostInspection;
    if (gapReconditionCost > 0) {
      return Math.abs(c2bOffer - gapReconditionCost);
    }
    return Math.abs(c2bOffer);
  }, [supervisor?.c2bOffer, reconditionCostCounterInspection, reconditionCostInspection]);

  const toggleAccordion = () => {
    setOpen(!open);
  };

  const toggleDetails = () => setOpenDetails(!openDetails);

  return {
    expertiseCosts,
    vehicle,
    vehicleExpertise,
    supervisor,
    counterInspection,
    reconditionCostInspection,
    reconditionCostCounterInspection,
    gapBodywork,
    gapServicing,
    gapWheels,
    gapInterior,
    gapRoadTest,
    gapMotor,
    countryPosId,
    newSuggestOffer,
    MileageGap,
    CounterAppraisalAnalys: !recordEntity.isNrmRecord() && recordEntity.status() === counterAppraisalVerification,
    tooltipOpenSecond,
    setTooltipOpenSecond,
    open,
    toggleAccordion,
    openDetails,
    toggleDetails,
  };
};
